import { useState } from 'react';
import { App, Button, Card, Radio, Space, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';

export type ListenerOnboardingMode = 'automatic' | 'manual';

/**
 * Which listener onboarding flow is live. Automatic: a new woman finishing
 * sign-up becomes a listener at once, and the app hides "Become a listener" for
 * everyone. Manual: the original Profile → Become a listener → application →
 * KYC → review/interview → approval flow.
 */
export function ListenerOnboardingCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { data } = useQuery({
    queryKey: ['settings', 'listener-onboarding'],
    queryFn: async () =>
      (await api.get<{ mode: ListenerOnboardingMode }>('/admin/settings/listener-onboarding')).data,
  });
  const [draft, setDraft] = useState<ListenerOnboardingMode | null>(null);

  const save = useMutation({
    mutationFn: async (mode: ListenerOnboardingMode) =>
      api.post('/admin/settings/listener-onboarding', { mode }),
    onSuccess: () => {
      message.success('Saved');
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['settings', 'listener-onboarding'] });
    },
    onError: () => message.error('Could not save'),
  });

  if (!data) return null;
  const value = draft ?? data.mode;

  return (
    <Card style={{ maxWidth: 640 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Radio.Group value={value} onChange={(e) => setDraft(e.target.value as ListenerOnboardingMode)}>
          <Space direction="vertical">
            <Radio value="automatic" data-testid="mode-automatic">
              Automatic — new women become listeners on sign-up
            </Radio>
            <Radio value="manual" data-testid="mode-manual">
              Manual — apply, KYC and admin approval
            </Radio>
          </Space>
        </Radio.Group>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          <b>Automatic:</b> a new user who picks Woman is made an approved listener as they finish
          onboarding — no application, KYC, interview or approval — and the app hides
          &ldquo;Become a listener&rdquo; for everyone. <b>Manual:</b> nobody is converted
          automatically; users apply from Profile &rarr; Become a listener and you review them on the
          Listeners and KYC pages. Switching never changes existing accounts: listeners already
          created stay listeners, and pending applications stay in the Listeners queue. The app
          picks up a change on its next session refresh.
        </Typography.Paragraph>
        <Can permission="listeners.approve">
          <Button
            type="primary"
            disabled={draft === null || draft === data.mode}
            loading={save.isPending}
            onClick={() => draft !== null && save.mutate(draft)}
          >
            Save
          </Button>
        </Can>
      </Space>
    </Card>
  );
}
